/**
 * Pure helpers for the Commitments tab (f11.4). Every conditional reads a
 * server field -- `pending_input`, `mode`, `business_units`, `results_pending`.
 * Nothing here decides visibility, editability or a time window.
 */

import type { ClientCommitments, ClientCycleCommitment, CommitmentCurrencyFigures } from '@/api/client';

export const EMPTY_MONEY = '—';

/** Tabular money: thousands separators, 0 dp, currency symbol (or code). Zero renders as `—`. */
export function formatMoney(amount: string, currencyCode: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) return EMPTY_MONEY;
  try {
    return value.toLocaleString(undefined, { style: 'currency', currency: currencyCode, maximumFractionDigits: 0 });
  } catch {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currencyCode}`;
  }
}

/** The FY revenue of one status in one currency's figures, or `null` when the bucket is absent. */
export function statusAmount(figures: CommitmentCurrencyFigures, statusCode: string): string | null {
  return figures.by_status.find((bucket) => bucket.status_code === statusCode)?.fy_revenue.amount ?? null;
}

/** The number of cycles with input pending for the viewer -- the tab badge (absent at zero). */
export function pendingCount(data: ClientCommitments | undefined): number | undefined {
  const count = data?.cycles.filter((cycle) => cycle.pending_input).length ?? 0;
  return count > 0 ? count : undefined;
}

/** Route A's grid URL, with the client preselected (BR-F11.4-4: a UI preselection, never authorization). */
export function forecastGridHref(cycleId: string, unitId: string, clientId: string): string {
  return (
    `/cycles/${encodeURIComponent(cycleId)}/forecast` +
    `?bu=${encodeURIComponent(unitId)}&client=${encodeURIComponent(clientId)}`
  );
}

/** "Open Aug–Dec 2026" beside the pending badge; nothing when no month is open. */
export function openWindowHint(item: ClientCycleCommitment): string | null {
  return item.cycle.open_window ? `Open ${item.cycle.open_window.label}` : null;
}
